import { useState, type ReactNode } from "react";
import { SharedAccess } from "target_wasm";
import { ModelElementType } from "@/components/entities";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { useTrashStore } from "@/components/main/stores/trashStore";
import { useTeamsStore } from "@/components/main/stores/teamsStore";
import { ConfirmDialog } from "@/components/main/utils/ConfirmDialog";
import { ShareModel } from "@/components/canvas/detailsSide/modelDetails/ShareModel";
import { dissociateModelFromTeam } from "@/components/main/utils/teams/TeamsApi";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import {
  renameModelRequest,
  handleDeleteModel,
  fetchModelTablesForDeletion,
  handleRestoreModel,
  handleHardDeleteModel,
} from "./ModelServices";
import { DeleteModelDialog } from "./deletion/DeleteModelDialog";
import {
  type TableDeletionAction,
  isScheduleDeletionAction,
} from "./deletion/DeleteModelDialogTypes";

export interface UseModelActionsOptions {
  isTeamContext?: boolean;
  teamId?: string;
  canDissociate?: boolean;
}

export interface ModelActionPermissions {
  canDeleteModel: boolean;
  canDissociateModel: boolean;
  canSeeSharedList: boolean;
  canManageAccess: boolean;
}

export interface ModelActions {
  permissions: ModelActionPermissions;
  /** Optimistic rename — applies immediately, rolls back with a toast on
   *  server failure. Returns true so editors can close right away. */
  commitName: (name: string) => boolean;
  /** True while the delete request is in flight (row shows a spinner). */
  isDeleting: boolean;
  copyModelId: () => Promise<void>;
  openShare: () => void;
  /** Analyse dependent tables, then delete immediately or open the dialog. */
  requestDelete: () => Promise<void>;
  restore: () => Promise<void>;
  requestHardDelete: () => void;
  dissociate: () => Promise<void>;
  /** Delete / share / hard-delete dialogs — render once in the caller's tree. */
  dialogs: ReactNode;
}

/**
 * Shared model-action logic (delete flow, share, copy ID, rename, plus the
 * trash/team variants) + permission derivations + the dialog elements. The
 * caller composes its own context menu from these; this hook just owns the
 * heavy/duplicated bits. Used by the overview `ModelRow`.
 */
export function useModelActions(
  model: ModelElementType,
  opts: UseModelActionsOptions = {},
): ModelActions {
  const { isTeamContext = false, teamId, canDissociate = false } = opts;

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hardDeleteDialogOpen, setHardDeleteDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharedWithList, setSharedWithList] = useState<SharedAccess>();

  const setSelectedModel = useMainStateStore((s) => s.setSelectedModel);
  const patchModel = useMainStateStore((s) => s.patchModel);
  const removeModelFromAvailable = useMainStateStore(
    (s) => s.removeModelFromAvailable,
  );
  const refreshTrashModels = useTrashStore((s) => s.refreshTrashModels);
  const removeModelFromTeam = useTeamsStore((s) => s.removeModelFromTeam);

  const modelName = model.name || "Unnamed Model";

  const commitName = (name: string): boolean => {
    const rollback = patchModel(model.model_id, { name });
    void renameModelRequest(model.model_id, name).then((ok) => {
      if (!ok) {
        rollback();
        showNotificationGlobal("Failed to rename model", "error");
      }
    });
    return true;
  };

  const handleDeleteModelWithActions = async (
    tableActions: Record<string, TableDeletionAction>,
  ): Promise<void> => {
    const tableIds = Object.entries(tableActions)
      .filter(([, action]) => isScheduleDeletionAction(action))
      .map(([tableId]) => tableId);
    setIsDeleting(true);
    try {
      const success = await handleDeleteModel(
        model.model_id,
        model.name,
        tableIds,
      );
      if (success) {
        removeModelFromAvailable(model.model_id);
        setSelectedModel(null);
        await refreshTrashModels();
        showNotificationGlobal("Model scheduled for deletion", "success");
      } else {
        showNotificationGlobal("Failed to delete model", "error");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const requestDelete = async (): Promise<void> => {
    try {
      const analysis = await fetchModelTablesForDeletion(model.model_id);
      const totalTables = analysis?.tables_js?.length || 0;
      if (totalTables === 0) {
        await handleDeleteModelWithActions({});
      } else {
        setDeleteDialogOpen(true);
      }
    } catch {
      setDeleteDialogOpen(true);
    }
  };

  const restore = async (): Promise<void> => {
    const success = await handleRestoreModel(model.model_id);
    if (success) {
      await Promise.all([
        refreshTrashModels(),
        useMainStateStore.getState().invalidateModels(),
      ]);
      showNotificationGlobal("Model restored from trash", "success");
    } else {
      showNotificationGlobal("Failed to restore model from trash", "error");
    }
  };

  const handleHardDeleteConfirm = async (): Promise<void> => {
    const success = await handleHardDeleteModel(model.model_id);
    setHardDeleteDialogOpen(false);
    if (success) {
      await refreshTrashModels();
      showNotificationGlobal("Model permanently deleted", "success");
    } else {
      showNotificationGlobal("Failed to permanently delete model", "error");
    }
  };

  const dissociate = async (): Promise<void> => {
    if (!teamId) return;
    try {
      await dissociateModelFromTeam(teamId, model.model_id);
      removeModelFromTeam(teamId, model.model_id);
      setSelectedModel(null);
      await Promise.all([
        useMainStateStore.getState().invalidateModels(),
        Promise.resolve(useTeamsStore.getState().invalidateTeamModels(teamId)),
      ]);
      showNotificationGlobal("Model dissociated from team", "success");
    } catch {
      showNotificationGlobal("Failed to dissociate model from team", "error");
    }
  };

  const copyModelId = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(model.model_id);
      showNotificationGlobal("Model ID copied to clipboard", "success");
    } catch {
      showNotificationGlobal("Couldn't copy the model ID", "error");
    }
  };

  const openShare = (): void => setShareDialogOpen(true);
  const requestHardDelete = (): void => setHardDeleteDialogOpen(true);

  const permissions: ModelActionPermissions = {
    canDeleteModel:
      !isTeamContext &&
      model.relation === "Owner" &&
      !model.scheduled_deletion_date,
    canDissociateModel:
      isTeamContext && (canDissociate || model.relation === "Owner"),
    canSeeSharedList:
      !model.scheduled_deletion_date &&
      (model.user_access?.includes("CanSeeSharedWithList") ?? false),
    canManageAccess:
      !model.scheduled_deletion_date &&
      (model.user_access?.includes("CanManageAccess") ?? false),
  };

  const dialogs = (
    <>
      {deleteDialogOpen && (
        <DeleteModelDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          model={model}
          onDelete={handleDeleteModelWithActions}
        />
      )}
      {shareDialogOpen && (
        <ShareModel
          modelID={model.model_id}
          modelName={modelName}
          role={undefined}
          sharedWith={sharedWithList}
          setIsShare={() => setShareDialogOpen(false)}
          canManageAccess={permissions.canManageAccess}
          setSharedWithList={setSharedWithList}
        />
      )}
      <ConfirmDialog
        open={hardDeleteDialogOpen}
        title="Permanently Delete Model"
        message={
          <p className="mb-2">
            Are you sure you want to permanently delete the model "{modelName}"?
          </p>
        }
        confirmText="Delete Permanently"
        cancelText="Cancel"
        onConfirm={handleHardDeleteConfirm}
        onCancel={() => setHardDeleteDialogOpen(false)}
      />
    </>
  );

  return {
    permissions,
    commitName,
    isDeleting,
    copyModelId,
    openShare,
    requestDelete,
    restore,
    requestHardDelete,
    dissociate,
    dialogs,
  };
}
